/**
 * Sites Page - Deployment Bay
 * ===========================
 * Manage all your sites/containers here.
 * Create, view, start, stop - it's all here!
 */

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, api } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { 
  Container, 
  Plus, 
  Search,
  Play,
  Square,
  RefreshCw,
  Trash2,
  Cpu,
  MemoryStick,
  HardDrive,
  ExternalLink,
  Code2,
  MoreVertical,
  ChevronRight,
  Loader2,
  Terminal,
  Activity,
  Brain,
  LogOut,
  Flame,
  Trophy
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Progress } from "../components/ui/progress";
import { toast } from "sonner";

// Sidebar Component (same as Dashboard)
const Sidebar = ({ activePage }) => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const menuItems = [
    { id: "dashboard", label: "MISSION CONTROL", icon: <Activity className="w-5 h-5" />, path: "/dashboard" },
    { id: "sites", label: "DEPLOYMENTS", icon: <Container className="w-5 h-5" />, path: "/sites" },
    { id: "ml-studio", label: "ROBOT GYM", icon: <Brain className="w-5 h-5" />, path: "/ml-studio" },
    { id: "tutorials", label: "GUIDES", icon: <Trophy className="w-5 h-5" />, path: "/tutorials" },
    { id: "family", label: "FAMILY", icon: <Flame className="w-5 h-5" />, path: "/family" },
  ];

  return (
    <div className="w-64 bg-[#16191F] border-r border-white/5 flex flex-col min-h-screen">
      <div className="p-6 border-b border-white/5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-primary rounded-sm flex items-center justify-center">
            <Terminal className="w-6 h-6 text-black" />
          </div>
          <span className="text-xl font-black text-white tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>
            CODEFORGE
          </span>
        </div>
      </div>

      <div className="p-4 m-4 bg-black/30 border border-primary/20">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-mono text-muted-foreground uppercase">LEVEL {user?.level || 1}</span>
          <span className="text-xs font-mono text-primary">{user?.xp || 0} XP</span>
        </div>
        <Progress value={(user?.xp || 0) % 100} className="h-2 bg-muted" />
        <div className="flex items-center gap-2 mt-3">
          <Flame className="w-4 h-4 text-orange-400" />
          <span className="text-xs font-mono text-white">{user?.streak || 0} day streak</span>
        </div>
      </div>

      <nav className="flex-1 p-4">
        <div className="space-y-1">
          {menuItems.map((item) => (
            <button
              key={item.id}
              onClick={() => navigate(item.path)}
              className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-mono transition-colors ${
                activePage === item.id
                  ? "bg-primary/10 text-primary border-l-2 border-primary"
                  : "text-muted-foreground hover:text-white hover:bg-white/5"
              }`}
              data-testid={`nav-${item.id}`}
            >
              {item.icon}
              {item.label}
            </button>
          ))}
        </div>
      </nav>

      <div className="p-4 border-t border-white/5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-primary/20 rounded-full flex items-center justify-center">
              <span className="text-primary font-bold text-sm">{user?.username?.[0]?.toUpperCase()}</span>
            </div>
            <span className="text-sm font-mono text-white">{user?.username}</span>
          </div>
          <button onClick={logout} className="text-muted-foreground hover:text-red-400 transition-colors" data-testid="logout-btn">
            <LogOut className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

const SitesPage = () => {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newSite, setNewSite] = useState({
    name: "",
    description: "",
    php_version: "8.2",
  });

  useEffect(() => {
    fetchSites();
  }, []);

  const fetchSites = async () => {
    try {
      const res = await api.get("/sites");
      setSites(res.data);
    } catch (error) {
      toast.error("Failed to load sites");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSite = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const res = await api.post("/sites", newSite);
      setSites([res.data, ...sites]);
      setShowCreateModal(false);
      setNewSite({ name: "", description: "", php_version: "8.2" });
      toast.success("Site created!", { description: "+20 XP earned!" });
      await refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create site");
    } finally {
      setCreating(false);
    }
  };

  const handleSiteAction = async (siteId, action) => {
    try {
      await api.post(`/sites/${siteId}/action?action=${action}`);
      await fetchSites();
      if (action === "start" || action === "restart") {
        toast.success(`Site ${action}ed!`, { description: "+25 XP earned!" });
        await refreshUser();
      } else {
        toast.success(`Site ${action}ped`);
      }
    } catch (error) {
      toast.error(`Failed to ${action} site`);
    }
  };

  const handleDeleteSite = async (siteId, siteName) => {
    if (!window.confirm(`Are you sure you want to delete "${siteName}"? This cannot be undone!`)) {
      return;
    }
    try {
      await api.delete(`/sites/${siteId}`);
      setSites(sites.filter(s => s.id !== siteId));
      toast.success("Site deleted");
    } catch (error) {
      toast.error("Failed to delete site");
    }
  };

  const filteredSites = sites.filter(site => 
    site.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    site.domain.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="flex min-h-screen bg-[#0B0D10]">
      <Sidebar activePage="sites" />
      
      <div className="flex-1 overflow-auto">
        {/* Header */}
        <header className="border-b border-white/5 bg-[#16191F]/50 backdrop-blur-sm sticky top-0 z-10">
          <div className="px-8 py-4 flex items-center justify-between">
            <div>
              <h1 
                className="text-2xl font-bold text-white uppercase tracking-tight"
                style={{ fontFamily: 'Chivo, sans-serif' }}
                data-testid="sites-title"
              >
                DEPLOYMENT BAY
              </h1>
              <p className="text-xs font-mono text-muted-foreground mt-1">
                {sites.length} site{sites.length !== 1 ? 's' : ''} deployed
              </p>
            </div>
            <Button
              onClick={() => setShowCreateModal(true)}
              className="bg-primary text-black font-bold hover:bg-primary/90"
              data-testid="create-site-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              NEW DEPLOYMENT
            </Button>
          </div>
        </header>

        {/* Main Content */}
        <main className="p-8">
          {/* Search */}
          <div className="mb-6">
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search deployments..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-[#16191F] border-white/10 font-mono"
                data-testid="search-sites-input"
              />
            </div>
          </div>

          {/* Sites Grid */}
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="text-center">
                <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mb-2" />
                <p className="text-muted-foreground font-mono">Loading deployments...</p>
              </div>
            </div>
          ) : filteredSites.length === 0 ? (
            <div className="bg-[#16191F] border border-white/5 p-12 text-center">
              <Container className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-xl font-bold text-white mb-2">
                {searchQuery ? "No sites found" : "No Deployments Yet"}
              </h3>
              <p className="text-muted-foreground font-mono text-sm mb-6">
                {searchQuery 
                  ? "Try a different search term" 
                  : "Create your first site to start building!"}
              </p>
              {!searchQuery && (
                <Button
                  onClick={() => setShowCreateModal(true)}
                  className="bg-primary text-black font-bold"
                  data-testid="create-first-site-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create Your First Site
                </Button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredSites.map((site) => (
                <div
                  key={site.id}
                  className="bg-[#16191F] border border-white/5 overflow-hidden hover:border-primary/30 transition-all group"
                  data-testid={`site-card-${site.id}`}
                >
                  {/* Card Header */}
                  <div className="p-5 border-b border-white/5">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-3">
                        <div className={`w-3 h-3 rounded-full ${site.status === 'running' ? 'bg-green-400 status-online' : 'bg-red-400'}`} />
                        <h3 className="font-bold text-white">{site.name}</h3>
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button className="text-muted-foreground hover:text-white p-1" data-testid={`site-menu-${site.id}`}>
                            <MoreVertical className="w-4 h-4" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="bg-[#16191F] border-white/10">
                          <DropdownMenuItem 
                            onClick={() => navigate(`/sites/${site.id}`)}
                            className="text-white hover:bg-white/10 font-mono text-sm cursor-pointer"
                          >
                            <ExternalLink className="w-4 h-4 mr-2" /> View Details
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={() => navigate(`/sites/${site.id}/files`)}
                            className="text-white hover:bg-white/10 font-mono text-sm cursor-pointer"
                          >
                            <Code2 className="w-4 h-4 mr-2" /> File Manager
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={() => handleDeleteSite(site.id, site.name)}
                            className="text-red-400 hover:bg-red-400/10 font-mono text-sm cursor-pointer"
                          >
                            <Trash2 className="w-4 h-4 mr-2" /> Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                    <p className="text-xs font-mono text-muted-foreground truncate">
                      {site.domain}
                    </p>
                  </div>

                  {/* Card Body */}
                  <div className="p-5">
                    <div className="grid grid-cols-3 gap-4 mb-4">
                      <div className="text-center">
                        <div className="flex items-center justify-center gap-1 text-muted-foreground mb-1">
                          <Cpu className="w-3 h-3" />
                        </div>
                        <div className="text-sm font-mono text-white">{site.cpu_usage}%</div>
                        <div className="text-xs text-muted-foreground">CPU</div>
                      </div>
                      <div className="text-center">
                        <div className="flex items-center justify-center gap-1 text-muted-foreground mb-1">
                          <MemoryStick className="w-3 h-3" />
                        </div>
                        <div className="text-sm font-mono text-white">{site.memory_usage}MB</div>
                        <div className="text-xs text-muted-foreground">RAM</div>
                      </div>
                      <div className="text-center">
                        <div className="flex items-center justify-center gap-1 text-muted-foreground mb-1">
                          <HardDrive className="w-3 h-3" />
                        </div>
                        <div className="text-sm font-mono text-white">{site.disk_usage}MB</div>
                        <div className="text-xs text-muted-foreground">DISK</div>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2">
                      {site.status === 'running' ? (
                        <>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleSiteAction(site.id, 'stop')}
                            className="flex-1 border-red-400/30 text-red-400 hover:bg-red-400/10 font-mono text-xs"
                            data-testid={`stop-site-${site.id}`}
                          >
                            <Square className="w-3 h-3 mr-1" /> STOP
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleSiteAction(site.id, 'restart')}
                            className="flex-1 border-blue-400/30 text-blue-400 hover:bg-blue-400/10 font-mono text-xs"
                            data-testid={`restart-site-${site.id}`}
                          >
                            <RefreshCw className="w-3 h-3 mr-1" /> RESTART
                          </Button>
                        </>
                      ) : (
                        <Button
                          size="sm"
                          onClick={() => handleSiteAction(site.id, 'start')}
                          className="flex-1 bg-green-500 text-black hover:bg-green-400 font-mono text-xs"
                          data-testid={`start-site-${site.id}`}
                        >
                          <Play className="w-3 h-3 mr-1" /> START
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Card Footer */}
                  <button
                    onClick={() => navigate(`/sites/${site.id}`)}
                    className="w-full p-3 border-t border-white/5 text-xs font-mono text-muted-foreground hover:text-primary hover:bg-white/5 transition-colors flex items-center justify-center gap-1"
                    data-testid={`view-site-${site.id}`}
                  >
                    View Details <ChevronRight className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </main>
      </div>

      {/* Create Site Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="bg-[#16191F] border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-white" style={{ fontFamily: 'Chivo, sans-serif' }}>
              NEW DEPLOYMENT
            </DialogTitle>
            <DialogDescription className="text-muted-foreground font-mono text-sm">
              Configure your new container
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleCreateSite} className="space-y-5 mt-4">
            <div className="space-y-2">
              <Label className="text-xs font-mono uppercase text-muted-foreground">
                SITE NAME
              </Label>
              <Input
                type="text"
                value={newSite.name}
                onChange={(e) => setNewSite({ ...newSite, name: e.target.value })}
                placeholder="My Awesome Site"
                className="bg-black/50 border-white/10 font-mono"
                required
                minLength={2}
                maxLength={50}
                data-testid="new-site-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-xs font-mono uppercase text-muted-foreground">
                DESCRIPTION (OPTIONAL)
              </Label>
              <Input
                type="text"
                value={newSite.description}
                onChange={(e) => setNewSite({ ...newSite, description: e.target.value })}
                placeholder="What's this site about?"
                className="bg-black/50 border-white/10 font-mono"
                data-testid="new-site-desc-input"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-xs font-mono uppercase text-muted-foreground">
                PHP VERSION
              </Label>
              <Select
                value={newSite.php_version}
                onValueChange={(value) => setNewSite({ ...newSite, php_version: value })}
              >
                <SelectTrigger className="bg-black/50 border-white/10 font-mono" data-testid="php-version-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#16191F] border-white/10">
                  <SelectItem value="8.2" className="font-mono">PHP 8.2 (Latest)</SelectItem>
                  <SelectItem value="8.1" className="font-mono">PHP 8.1</SelectItem>
                  <SelectItem value="8.0" className="font-mono">PHP 8.0</SelectItem>
                  <SelectItem value="7.4" className="font-mono">PHP 7.4 (Legacy)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowCreateModal(false)}
                className="flex-1 border-white/10 font-mono"
                data-testid="cancel-create-btn"
              >
                CANCEL
              </Button>
              <Button
                type="submit"
                disabled={creating}
                className="flex-1 bg-primary text-black font-bold hover:bg-primary/90"
                data-testid="submit-create-btn"
              >
                {creating ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    DEPLOYING...
                  </>
                ) : (
                  <>
                    <Container className="w-4 h-4 mr-2" />
                    DEPLOY
                  </>
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SitesPage;
